import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [posting, setPosting] = useState(false);
  
  // Form states
  const [inputType, setInputType] = useState('text'); // text, image, voice
  const [textPrompt, setTextPrompt] = useState('');
  const [platform, setPlatform] = useState('facebook');
  const [tone, setTone] = useState('casual');
  const [generatedPost, setGeneratedPost] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const initDashboard = async () => {
      await checkAuth();
      await checkSubscription();
      await loadConnections();
      
      // Check for subscription confirmation
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');
      if (sessionId) {
        await confirmSubscription(sessionId);
      }
    };
    
    initDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        navigate('/login');
        return;
      }
      
      const data = await response.json();
      setUser(data);
    } catch (err) {
      // Auth failed - redirect to login
      navigate('/login');
    }
  };

  const checkSubscription = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/subscriptions/status`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      }
    } catch (err) {
      console.error('Subscription check failed:', err);
      // Subscription check failed - continue with no subscription
    } finally {
      setLoading(false);
    }
  };

  const confirmSubscription = async (sessionId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/subscriptions/confirm/${sessionId}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        setSuccess('🎉 Subscription activated! Welcome to AI Auto-Poster!');
        setTimeout(() => checkSubscription(), 1000);
      }
    } catch (err) {
      console.error('Subscription confirmation failed:', err);
      setError('Failed to confirm subscription. Please refresh the page.');
    }
  };

  const loadConnections = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/social/connections`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setConnections(data.connections || []);
      }
    } catch (err) {
      console.error('Failed to load social connections:', err);
      // Failed to load connections - continue with empty list
    }
  };

  const startSubscription = async () => {
    try {
      const origin = window.location.origin;
      const response = await fetch(`${BACKEND_URL}/api/subscriptions/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ origin_url: origin })
      });
      
      if (!response.ok) throw new Error('Failed to create subscription');
      
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      setError('Failed to start subscription: ' + err.message);
    }
  };

  const connectSocial = async (platformName) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/social/connect/${platformName}`, {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to initiate OAuth');
      
      const data = await response.json();
      window.location.href = data.auth_url;
    } catch (err) {
      setError(`Failed to connect ${platformName}: ` + err.message);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/mp3' });
        setAudioBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (err) {
      setError('Failed to start recording: ' + err.message);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const generatePost = async () => {
    setError('');
    setSuccess('');
    setGenerating(true);

    try {
      const requestBody = {
        platform,
        tone
      };

      if (inputType === 'text' && textPrompt) {
        requestBody.prompt = textPrompt;
      } else if (inputType === 'image' && imageFile) {
        const reader = new FileReader();
        const base64 = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(imageFile);
        });
        requestBody.image_base64 = base64;
      } else if (inputType === 'voice' && audioBlob) {
        const reader = new FileReader();
        const base64 = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(audioBlob);
        });
        requestBody.audio_base64 = base64;
      } else {
        setError('Please provide input (text, image, or voice)');
        setGenerating(false);
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/ai/generate-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Generation failed');
      }

      const data = await response.json();
      setGeneratedPost(data.post_text);
      setSuccess(`✨ Generated ${data.character_count} character post!`);
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const postToSocial = async () => {
    if (!generatedPost) {
      setError('Please generate a post first');
      return;
    }

    setError('');
    setSuccess('');
    setPosting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/social/post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          platform,
          text: generatedPost
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Posting failed');
      }

      const data = await response.json();
      setSuccess(`🎉 ${data.message}`);
      setGeneratedPost('');
      setTextPrompt('');
      setImageFile(null);
      setImagePreview('');
      setAudioBlob(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setPosting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-2xl font-semibold text-gray-600">Loading...</div>
      </div>
    );
  }

  // No subscription - show upgrade prompt
  if (!subscription || !subscription.has_subscription) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="border-2 border-blue-200 shadow-xl">
            <CardHeader className="text-center">
              <CardTitle className="text-4xl font-bold text-gray-900 mb-4">
                🚀 AI Social Media Auto-Poster
              </CardTitle>
              <CardDescription className="text-xl text-gray-600">
                Automate your social media with the power of AI
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-8 rounded-lg">
                <div className="text-center mb-6">
                  <div className="text-5xl font-bold mb-2">$29.99</div>
                  <div className="text-xl opacity-90">per month</div>
                </div>
                
                <ul className="space-y-4 text-lg">
                  <li className="flex items-start">
                    <span className="mr-3">✨</span>
                    <span><strong>AI Content Generation:</strong> Text, Voice, or Image to Post</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3">🎤</span>
                    <span><strong>Voice-to-Text:</strong> Speak your ideas, AI writes the post</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3">📸</span>
                    <span><strong>Image Analysis:</strong> Upload a photo, AI creates the perfect caption</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3">🔗</span>
                    <span><strong>Facebook & Instagram:</strong> Connect your accounts and auto-post</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3">🎯</span>
                    <span><strong>Multiple Tones:</strong> Casual, Professional, or Friendly voice</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3">⚡</span>
                    <span><strong>Instant Results:</strong> Generate and post in seconds</span>
                  </li>
                </ul>
              </div>

              <Button 
                onClick={startSubscription}
                size="lg"
                className="w-full text-xl py-8 bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 transform hover:scale-105 transition-all"
              >
                🎉 Start Your $29.99/Month Subscription Now
              </Button>

              <p className="text-center text-gray-500 text-sm">
                Cancel anytime. No hidden fees. Start making content magic today!
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Has subscription - show dashboard
  const hasConnection = (platformName) => {
    return connections.some(c => c.platform === platformName && c.is_active);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8 px-4">
      <div className="max-w-6xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            🚀 AI Social Media Auto-Poster
          </h1>
          <p className="text-gray-600">Welcome back, {user?.name}! Create amazing content in seconds.</p>
          <p className="text-xs text-orange-600 italic mt-1">⚡ Powered by Kari Future Now Creations™</p>
          <Button
            onClick={() => navigate('/ai-lab')}
            data-testid="dashboard-go-to-ailab"
            className="mt-4 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold shadow-lg"
          >
            🚀 Open the AI Lab — 8 Superpowers Inside
          </Button>
        </div>

        {/* Alerts */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {success && (
          <Alert className="bg-green-50 border-green-200">
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}

        {/* Social Connections */}
        <Card>
          <CardHeader>
            <CardTitle>🔗 Connected Accounts</CardTitle>
            <CardDescription>Connect your social media accounts to start auto-posting</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <Button
                onClick={() => connectSocial('facebook')}
                variant={hasConnection('facebook') ? 'outline' : 'default'}
                className="w-full"
                disabled={hasConnection('facebook')}
              >
                {hasConnection('facebook') ? '✅ Facebook Connected' : '📘 Connect Facebook'}
              </Button>
              
              <Button
                onClick={() => connectSocial('instagram')}
                variant={hasConnection('instagram') ? 'outline' : 'default'}
                className="w-full"
                disabled={hasConnection('instagram')}
              >
                {hasConnection('instagram') ? '✅ Instagram Connected' : '📷 Connect Instagram'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* AI Generator */}
        <Card>
          <CardHeader>
            <CardTitle>✨ AI Content Generator</CardTitle>
            <CardDescription>Choose your input method and let AI create amazing posts</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            
            {/* Input Type Selection */}
            <div className="flex gap-4">
              <Button
                variant={inputType === 'text' ? 'default' : 'outline'}
                onClick={() => setInputType('text')}
                className="flex-1"
              >
                ✍️ Text
              </Button>
              <Button
                variant={inputType === 'image' ? 'default' : 'outline'}
                onClick={() => setInputType('image')}
                className="flex-1"
              >
                📸 Image
              </Button>
              <Button
                variant={inputType === 'voice' ? 'default' : 'outline'}
                onClick={() => setInputType('voice')}
                className="flex-1"
              >
                🎤 Voice
              </Button>
            </div>

            {/* Text Input */}
            {inputType === 'text' && (
              <Textarea
                placeholder="Describe your post idea... (e.g., 'Promote my new fitness program')"
                value={textPrompt}
                onChange={(e) => setTextPrompt(e.target.value)}
                rows={4}
                className="w-full"
              />
            )}

            {/* Image Input */}
            {inputType === 'image' && (
              <div className="space-y-4">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleImageUpload}
                  className="w-full"
                />
                {imagePreview && (
                  <img src={imagePreview} alt="Preview" className="max-w-md rounded-lg border" />
                )}
              </div>
            )}

            {/* Voice Input */}
            {inputType === 'voice' && (
              <div className="space-y-4">
                {!isRecording && !audioBlob && (
                  <Button onClick={startRecording} className="w-full" variant="outline">
                    🎤 Start Recording
                  </Button>
                )}
                {isRecording && (
                  <Button onClick={stopRecording} className="w-full" variant="destructive">
                    ⏹️ Stop Recording
                  </Button>
                )}
                {audioBlob && (
                  <div className="text-center text-green-600 font-semibold">
                    ✅ Audio recorded! Click Generate to create your post.
                  </div>
                )}
              </div>
            )}

            {/* Settings */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Platform</label>
                <Select value={platform} onValueChange={setPlatform}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="facebook">📘 Facebook</SelectItem>
                    <SelectItem value="instagram">📷 Instagram</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Tone</label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="casual">😊 Casual</SelectItem>
                    <SelectItem value="professional">💼 Professional</SelectItem>
                    <SelectItem value="friendly">🤗 Friendly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Generate Button */}
            <Button
              onClick={generatePost}
              disabled={generating}
              className="w-full bg-gradient-to-r from-purple-500 to-blue-600 hover:from-purple-600 hover:to-blue-700"
              size="lg"
            >
              {generating ? '✨ Generating...' : '✨ Generate AI Post'}
            </Button>

            {/* Generated Post Preview */}
            {generatedPost && (
              <div className="space-y-4 p-6 bg-gray-50 rounded-lg border-2 border-purple-200">
                <div className="font-semibold text-gray-700">Your AI-Generated Post:</div>
                <Textarea
                  value={generatedPost}
                  onChange={(e) => setGeneratedPost(e.target.value)}
                  rows={6}
                  className="w-full bg-white"
                />
                <div className="text-sm text-gray-500">
                  {generatedPost.length} characters
                </div>
                
                <Button
                  onClick={postToSocial}
                  disabled={posting || !hasConnection(platform)}
                  className="w-full bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700"
                  size="lg"
                >
                  {posting ? '📤 Posting...' : `📤 Post to ${platform === 'facebook' ? 'Facebook' : 'Instagram'}`}
                </Button>

                {!hasConnection(platform) && (
                  <p className="text-red-600 text-sm text-center">
                    Please connect your {platform} account above to post
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
